import type { Appearance } from '@clerk/types';
import type { LocalizationResource } from '@clerk/types';
import logger from '../utils/logger';

export const CLERK_PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
export const CLERK_SECRET_KEY = import.meta.env.VITE_CLERK_SECRET_KEY;

// Clerk localization for all supported languages
const clerkLocalizations: Record<string, LocalizationResource> = {
  en: {
    socialButtonsBlockButton: "Continue with {{provider|titleize}}",
    dividerText: "or",
    formFieldLabel__emailAddress: "Email address",
    formFieldLabel__password: "Password",
    formFieldLabel__firstName: "First name",
    formFieldLabel__lastName: "Last name",
    formButtonPrimary: "Continue",
    signIn: {
      start: {
        title: "Sign in",
        subtitle: "to continue to IPTVPlayer.stream",
        actionText: "No account?",
        actionLink: "Sign up"
      }
    },
    signUp: {
      start: {
        title: "Create account",
        subtitle: "to continue to IPTVPlayer.stream",
        actionText: "Already have an account?",
        actionLink: "Sign in"
      }
    }
  },
  es: {
    socialButtonsBlockButton: "Continuar con {{provider|titleize}}",
    dividerText: "o",
    formFieldLabel__emailAddress: "Correo electrónico",
    formFieldLabel__password: "Contraseña",
    formFieldLabel__firstName: "Nombre",
    formFieldLabel__lastName: "Apellido",
    formButtonPrimary: "Continuar",
    signIn: {
      start: {
        title: "Iniciar sesión",
        subtitle: "para continuar a IPTVPlayer.stream",
        actionText: "¿No tienes cuenta?",
        actionLink: "Regístrate"
      }
    },
    signUp: {
      start: {
        title: "Crear cuenta",
        subtitle: "para continuar a IPTVPlayer.stream",
        actionText: "¿Ya tienes una cuenta?",
        actionLink: "Iniciar sesión"
      }
    }
  },
  fr: {
    socialButtonsBlockButton: "Continuer avec {{provider|titleize}}",
    dividerText: "ou",
    formFieldLabel__emailAddress: "Adresse e-mail",
    formFieldLabel__password: "Mot de passe",
    formFieldLabel__firstName: "Prénom",
    formFieldLabel__lastName: "Nom",
    formButtonPrimary: "Continuer",
    signIn: {
      start: {
        title: "Connexion",
        subtitle: "pour continuer vers IPTVPlayer.stream",
        actionText: "Pas de compte ?",
        actionLink: "S'inscrire"
      }
    },
    signUp: {
      start: {
        title: "Créer un compte",
        subtitle: "pour continuer vers IPTVPlayer.stream",
        actionText: "Vous avez déjà un compte ?",
        actionLink: "Se connecter"
      }
    }
  },
  de: {
    socialButtonsBlockButton: "Weiter mit {{provider|titleize}}",
    dividerText: "oder",
    formFieldLabel__emailAddress: "E-Mail-Adresse",
    formFieldLabel__password: "Passwort",
    formFieldLabel__firstName: "Vorname",
    formFieldLabel__lastName: "Nachname",
    formButtonPrimary: "Weiter",
    signIn: {
      start: {
        title: "Anmelden",
        subtitle: "um zu IPTVPlayer.stream fortzufahren",
        actionText: "Kein Konto?",
        actionLink: "Registrieren"
      }
    },
    signUp: {
      start: {
        title: "Konto erstellen",
        subtitle: "um zu IPTVPlayer.stream fortzufahren",
        actionText: "Bereits ein Konto?",
        actionLink: "Anmelden"
      }
    }
  },
  it: {
    socialButtonsBlockButton: "Continua con {{provider|titleize}}",
    dividerText: "oppure",
    formFieldLabel__emailAddress: "Indirizzo email",
    formFieldLabel__password: "Password",
    formFieldLabel__firstName: "Nome",
    formFieldLabel__lastName: "Cognome",
    formButtonPrimary: "Continua",
    signIn: {
      start: {
        title: "Accedi",
        subtitle: "per continuare su IPTVPlayer.stream",
        actionText: "Non hai un account?",
        actionLink: "Registrati"
      }
    },
    signUp: {
      start: {
        title: "Crea account",
        subtitle: "per continuare su IPTVPlayer.stream",
        actionText: "Hai già un account?",
        actionLink: "Accedi"
      }
    }
  },
  pt: {
    socialButtonsBlockButton: "Continuar com {{provider|titleize}}",
    dividerText: "ou",
    formFieldLabel__emailAddress: "Endereço de email",
    formFieldLabel__password: "Senha",
    formFieldLabel__firstName: "Nome",
    formFieldLabel__lastName: "Sobrenome",
    formButtonPrimary: "Continuar",
    signIn: {
      start: {
        title: "Entrar",
        subtitle: "para continuar para IPTVPlayer.stream",
        actionText: "Não tem uma conta?",
        actionLink: "Cadastre-se"
      }
    },
    signUp: {
      start: {
        title: "Criar conta",
        subtitle: "para continuar para IPTVPlayer.stream",
        actionText: "Já tem uma conta?",
        actionLink: "Entrar"
      }
    }
  },
  ar: {
    socialButtonsBlockButton: "المتابعة باستخدام {{provider|titleize}}",
    dividerText: "أو",
    formFieldLabel__emailAddress: "البريد الإلكتروني",
    formFieldLabel__password: "كلمة المرور",
    formFieldLabel__firstName: "الاسم الأول",
    formFieldLabel__lastName: "اسم العائلة",
    formButtonPrimary: "متابعة",
    signIn: {
      start: {
        title: "تسجيل الدخول",
        subtitle: "للمتابعة إلى IPTVPlayer.stream",
        actionText: "ليس لديك حساب؟",
        actionLink: "إنشاء حساب"
      }
    },
    signUp: {
      start: {
        title: "إنشاء حساب",
        subtitle: "للمتابعة إلى IPTVPlayer.stream",
        actionText: "لديك حساب بالفعل؟",
        actionLink: "تسجيل الدخول"
      }
    }
  },
  nl: {
    socialButtonsBlockButton: "Doorgaan met {{provider|titleize}}",
    dividerText: "of",
    formFieldLabel__emailAddress: "E-mailadres",
    formFieldLabel__password: "Wachtwoord",
    formFieldLabel__firstName: "Voornaam",
    formFieldLabel__lastName: "Achternaam",
    formButtonPrimary: "Doorgaan",
    signIn: {
      start: {
        title: "Inloggen",
        subtitle: "om door te gaan naar IPTVPlayer.stream",
        actionText: "Geen account?",
        actionLink: "Registreren"
      }
    },
    signUp: {
      start: {
        title: "Account aanmaken",
        subtitle: "om door te gaan naar IPTVPlayer.stream",
        actionText: "Heb je al een account?",
        actionLink: "Inloggen"
      }
    }
  },
  ru: {
    socialButtonsBlockButton: "Продолжить с {{provider|titleize}}",
    dividerText: "или",
    formFieldLabel__emailAddress: "Электронная почта",
    formFieldLabel__password: "Пароль",
    formFieldLabel__firstName: "Имя",
    formFieldLabel__lastName: "Фамилия",
    formButtonPrimary: "Продолжить",
    signIn: {
      start: {
        title: "Вход",
        subtitle: "для продолжения в IPTVPlayer.stream",
        actionText: "Нет аккаунта?",
        actionLink: "Зарегистрироваться"
      }
    },
    signUp: {
      start: {
        title: "Создать аккаунт",
        subtitle: "для продолжения в IPTVPlayer.stream",
        actionText: "Уже есть аккаунт?",
        actionLink: "Войти"
      }
    }
  },
  tr: {
    socialButtonsBlockButton: "{{provider|titleize}} ile devam et",
    dividerText: "veya",
    formFieldLabel__emailAddress: "E-posta adresi",
    formFieldLabel__password: "Şifre",
    formFieldLabel__firstName: "Ad",
    formFieldLabel__lastName: "Soyad",
    formButtonPrimary: "Devam et",
    signIn: {
      start: {
        title: "Giriş yap",
        subtitle: "IPTVPlayer.stream'e devam etmek için",
        actionText: "Hesabınız yok mu?",
        actionLink: "Kaydol"
      }
    },
    signUp: {
      start: {
        title: "Hesap oluştur",
        subtitle: "IPTVPlayer.stream'e devam etmek için",
        actionText: "Zaten hesabınız var mı?",
        actionLink: "Giriş yap"
      }
    }
  },
  pl: {
    socialButtonsBlockButton: "Kontynuuj z {{provider|titleize}}",
    dividerText: "lub",
    formFieldLabel__emailAddress: "Adres email",
    formFieldLabel__password: "Hasło",
    formFieldLabel__firstName: "Imię",
    formFieldLabel__lastName: "Nazwisko",
    formButtonPrimary: "Kontynuuj",
    signIn: {
      start: {
        title: "Zaloguj się",
        subtitle: "aby kontynuować do IPTVPlayer.stream",
        actionText: "Nie masz konta?",
        actionLink: "Zarejestruj się"
      }
    },
    signUp: {
      start: {
        title: "Utwórz konto",
        subtitle: "aby kontynuować do IPTVPlayer.stream",
        actionText: "Masz już konto?",
        actionLink: "Zaloguj się"
      }
    }
  },
  id: {
    socialButtonsBlockButton: "Lanjutkan dengan {{provider|titleize}}",
    dividerText: "atau",
    formFieldLabel__emailAddress: "Alamat email",
    formFieldLabel__password: "Kata sandi",
    formFieldLabel__firstName: "Nama depan",
    formFieldLabel__lastName: "Nama belakang",
    formButtonPrimary: "Lanjutkan",
    signIn: {
      start: {
        title: "Masuk",
        subtitle: "untuk melanjutkan ke IPTVPlayer.stream",
        actionText: "Belum punya akun?",
        actionLink: "Daftar"
      }
    },
    signUp: {
      start: {
        title: "Buat akun",
        subtitle: "untuk melanjutkan ke IPTVPlayer.stream",
        actionText: "Sudah punya akun?",
        actionLink: "Masuk"
      }
    }
  }
};

// Clerk configuration options
export const clerkConfig = {
  appearance: {
    elements: {
      rootBox: 'w-full sm:max-w-md mx-auto px-4 sm:px-0',
      card: 'bg-white dark:bg-[#1C2333] shadow-sm w-full sm:w-auto sm:min-w-[400px] max-w-full overflow-hidden',
      form: 'w-full',
      formButtonPrimary: 'bg-blue-500 hover:bg-blue-600 text-white w-full',
      formFieldInput: 'bg-white dark:bg-[#1C2333] border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white w-full',
      formFieldLabel: 'text-gray-700 dark:text-white',
      headerTitle: 'text-gray-900 dark:text-white text-xl sm:text-2xl',
      headerSubtitle: 'text-gray-600 dark:text-white text-sm sm:text-base',
      socialButtonsBlockButton: 'bg-white dark:bg-[#1C2333] hover:bg-gray-50 dark:hover:bg-[#252D3F] border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-white w-full',
      socialButtonsBlockButtonText: 'text-gray-600 dark:text-white text-sm sm:text-base',
      socialButtonsBlockButtonArrow: 'text-gray-600 dark:text-white',
      footerActionText: 'text-gray-600 dark:text-white text-sm sm:text-base block text-center mb-4',
      footerActionLink: 'text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 text-sm sm:text-base block text-center',
      footer: 'flex flex-col items-center gap-2',
      identityPreviewText: 'text-gray-700 dark:text-white',
      identityPreviewEditButton: 'text-blue-500 hover:text-blue-600 dark:text-blue-400',
      formFieldErrorText: 'text-red-600 dark:text-red-400 text-sm',
      formFieldSuccessText: 'text-green-600 dark:text-green-400 text-sm',
      dividerText: 'text-gray-600 dark:text-white',
      formFieldInputShowPasswordButton: 'text-gray-600 dark:text-white',
      otpCodeFieldInput: 'bg-white dark:bg-[#1C2333] border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white w-full',
      root: 'dark:bg-[#111827]',
      alternativeMethodsBlockButton: 'text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300',
      alternativeMethodsBlockButtonText: 'text-gray-700 dark:text-white font-medium text-sm sm:text-base',
      alternativeMethodsBlockButtonIcon: 'text-gray-600 dark:text-white',
      footerActionLink__processing: 'text-blue-500 dark:text-blue-400',
      footerActionText__processing: 'text-gray-700 dark:text-white',
      processingMessage: 'text-gray-700 dark:text-white text-sm sm:text-base',
      formFieldAction: 'text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300',
      formResendCodeLink: 'text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300',
      formFieldHintText: 'text-blue-500 dark:text-blue-400 text-sm font-medium',
      formFieldInfoText: 'text-blue-500 dark:text-blue-400 text-sm font-medium',
      verificationCodeInput: 'bg-white dark:bg-[#1C2333] border-gray-200 dark:border-gray-700 text-blue-500 dark:text-blue-400 w-full font-medium',
      formFieldLabelRow: 'text-blue-500 dark:text-blue-400 font-medium',
      formFieldInputGroup: 'text-blue-500 dark:text-blue-400 font-medium'
    },
    layout: {
      socialButtonsPlacement: 'bottom',
      socialButtonsVariant: 'iconButton',
      termsPageUrl: 'https://iptvplayer.stream/terms',
      privacyPageUrl: 'https://iptvplayer.stream/privacy',
      showOptionalFields: true,
      shimmer: true
    },
    variables: {
      colorPrimary: '#3b82f6',
      colorTextOnPrimaryBackground: '#ffffff',
      colorBackground: 'transparent',
      colorTextSecondary: '#3b82f6',
      colorInputBackground: 'transparent',
      colorText: '#3b82f6',
      colorInputText: '#3b82f6',
      colorDanger: '#dc2626',
      colorSuccess: '#16a34a',
      borderRadius: '0.5rem',
      spacingUnit: '1rem'
    }
  } satisfies Appearance,
  signInUrl: '/sign-in',
  signUpUrl: '/sign-up',
  afterSignInUrl: '/',
  afterSignUpUrl: '/',
  verifyEmailPath: '/verify-email',
  domain: 'iptvplayer.stream',
  isSatellite: true,
  signIn: {
    path: '/sign-in',
    routing: 'path',
    afterSignInUrl: '/',
    redirectUrl: '/'
  },
  signUp: {
    path: '/sign-up',
    routing: 'path',
    afterSignUpUrl: '/',
    redirectUrl: '/',
    attributes: {
      turnstile: {
        theme: 'dark',
        size: 'invisible'
      }
    },
    verifications: {
      emailAddress: {
        required: true,
        strategy: "email_code"
      }
    }
  },
  routing: {
    path: '/sign-in',
    signUpPath: '/sign-up',
    signInPath: '/sign-in',
    afterSignInUrl: '/',
    afterSignUpUrl: '/',
    redirectUrl: '/'
  },
  localization: {
    resources: clerkLocalizations
  }
}; 